/**
 * The wire shapes of `Rws.AiInsights.Host.Administration.BedrockModelManagementEndpoints` (AIA-109 model management, phases 4, 4b and 5):
 * the Bedrock access groups, one group's models and policy state, the update, and the take-over of a group the portal did not write.
 *
 * It does not use `adminRequest`, because these endpoints answer problems that must be told apart (409 `policy-changed`, `not-foreign`,
 * `no-policy`, `takeover-not-confirmed` and `empty-not-confirmed`; 422 `too-large`, `unknown-ids`, `unknown-provider`, `exception-outside-allow`, `redundant-ids`, `foreign-policy` and `refused`; 423 `writes-off`,
 * `kill-switch` and `renderer-not-configured`; 503 `renderer-busy` and `unavailable`), each with extension members the page acts on. AWS is
 * the only source of truth: nothing here is cached or stored in the browser.
 */
import { apiFetch } from "./apiFetch";

/** One configured Bedrock access group, as the group list shows it. */
export interface BedrockGroupSummary {
  name: string;
  description: string;
}

/** One model id as the group detail lists it. `route` is `eu`, `global` or `regional`. */
export interface BedrockModel {
  id: string;
  displayName: string;
  route: string;
  /** False when the account no longer lists the id but the group's policy still allows it. */
  available: boolean;
  /** Effective access: allowed by a single id or a provider allow, and not excepted. A legacy model covered only by a provider allow is not allowed. */
  allowed: boolean;
  /** How an allow reaches the model: `single`, `provider`, or null when none does. Says nothing about exceptions. */
  via?: "single" | "provider" | null;
}

/** The models of one family. */
export interface BedrockModelFamily {
  name: string;
  models: BedrockModel[];
}

/** The characters one id adds to the policy, by route; a live estimate, never the server's verdict. */
export interface BedrockEstimate {
  eu: number;
  global: number;
  regional: number;
}

/** One provider allowed on one route (`eu` or `global`); `hasModels` is false when no model of that provider is listed on the route. */
export interface BedrockProviderAllow {
  route: string;
  provider: string;
  hasModels: boolean;
}

/** The request form of a provider allow. */
export interface BedrockProviderAllowRequest {
  route: string;
  provider: string;
}

/** The policy size against its limit, in characters. */
export interface BedrockCapacity {
  limit: number;
  used: number;
  estimatePerId: BedrockEstimate;
  /** The characters one provider allow adds, by route. */
  estimatePerProvider?: { eu: number; global: number } | null;
  /** The characters one exception adds. */
  estimatePerException?: number;
}

/** `enabled`, or why an update cannot change anything now. */
export type BedrockWriteState = "enabled" | "writesOff" | "killSwitch" | "rendererNotConfigured";

/** One group's models and policy state (`BedrockGroupModelsResponse`). */
export interface BedrockGroupDetail {
  group: string;
  families: BedrockModelFamily[];
  /** The single ids only; models a provider allow covers are not listed here. */
  allowedIds: string[];
  /** The providers allowed as a whole, per route. */
  providerAllows?: BedrockProviderAllow[];
  /** Models switched off inside a provider allow; an exception applies to every group of a person. */
  deniedIds?: string[];
  /** The token to send back as `expectedPolicyHash`: 64 hex characters, or `none` for a group with no policy. */
  policyHash: string;
  /** The group's policy is one the portal did not write; it can only be replaced by a take-over. */
  foreign: boolean;
  noPolicy: boolean;
  capacity: BedrockCapacity | null;
  capacityUnavailableReason: string | null;
  /** A string from the server; values other than the four known ones are treated as not writable. */
  writeState: string;
  /** For a foreign group: the policy document as it is, for display only. */
  currentDocument?: unknown;
  /** For a foreign group: ids the policy appears to allow. Labels only; nothing is decided from them. */
  currentlyAppearsToAllow?: string[] | null;
  /** True when the host answers from local recordings: nothing is written to AWS. */
  replay?: boolean;
}

/** The read-only preview of a take-over (`BedrockTakeoverPreviewResponse`). */
export interface BedrockTakeoverPreview {
  currentDocument?: unknown;
  currentPolicyHash: string;
  currentlyAppearsToAllow: string[];
  proposedDocument: unknown;
  size: number;
  limit: number;
  removedFromDisplay: string[];
  addedFromDisplay: string[];
  writeState: string;
}

/** A refusal the page tells apart: the status, the stable `reason`, and the members that go with it. */
export interface BedrockProblem {
  kind: "problem";
  status: number;
  reason: string;
  message: string;
  /** The group's current state, when the refusal carries it (409 `policy-changed`). */
  current?: BedrockGroupDetail;
  unknownIds: string[];
  /** `unknown-provider`: the provider allows no listed model backs. */
  unknownProviders: BedrockProviderAllowRequest[];
  /** `exception-outside-allow`: the excepted ids no provider allow covers. */
  outsideAllowIds: string[];
  /** `redundant-ids`: the single ids a provider allow already covers. */
  redundantIds: string[];
  size?: number;
  limit?: number;
  addedFromDisplay: string[];
  removedFromDisplay: string[];
}

/** The selection a group is sent: single ids, plus provider allows and exceptions when there are any (omitted when empty, as before). */
export interface BedrockSelection {
  allowedIds: string[];
  providerAllows?: BedrockProviderAllowRequest[];
  deniedIds?: string[];
}

/** The outcome of one call. */
export type BedrockResult<TResponse> =
  | { kind: "ok"; response: TResponse }
  | { kind: "notFound"; message: string }
  | { kind: "denied" }
  | BedrockProblem
  | { kind: "error"; message: string };

interface ProblemBody {
  detail?: string;
  title?: string;
  reason?: string;
  current?: BedrockGroupDetail;
  unknownIds?: string[];
  unknownProviders?: BedrockProviderAllowRequest[];
  outsideAllowIds?: string[];
  redundantIds?: string[];
  size?: number;
  limit?: number;
  addedFromDisplay?: string[];
  removedFromDisplay?: string[];
}

const groupsPath = "/api/admin/bedrock/models/groups";
const groupPath = (group: string): string => `${groupsPath}/${encodeURIComponent(group)}`;

async function modelsRequest<TResponse>(path: string, init: RequestInit): Promise<BedrockResult<TResponse>> {
  try {
    const headers: HeadersInit = { Accept: "application/json", ...(init.body ? { "Content-Type": "application/json" } : {}) };
    const response = await apiFetch(path, { credentials: "same-origin", cache: "no-store", ...init, headers });

    if (response.ok) {
      return { kind: "ok", response: (await response.json()) as TResponse };
    }

    let body: ProblemBody = {};
    try {
      body = (await response.json()) as ProblemBody;
    } catch {
      body = {};
    }

    const message = body.detail ?? body.title ?? "";
    if (response.status === 403) {
      return { kind: "denied" };
    }

    if (response.status === 404) {
      return { kind: "notFound", message };
    }

    if (response.status === 400 || response.status === 409 || response.status === 422 || response.status === 423 || response.status === 503) {
      return {
        kind: "problem",
        status: response.status,
        reason: body.reason ?? "",
        message,
        current: body.current,
        unknownIds: body.unknownIds ?? [],
        unknownProviders: body.unknownProviders ?? [],
        outsideAllowIds: body.outsideAllowIds ?? [],
        redundantIds: body.redundantIds ?? [],
        size: body.size,
        limit: body.limit,
        addedFromDisplay: body.addedFromDisplay ?? [],
        removedFromDisplay: body.removedFromDisplay ?? [],
      };
    }

    return { kind: "error", message };
  } catch {
    return { kind: "error", message: "" };
  }
}

/** Calls `GET /api/admin/bedrock/models/groups`. */
export async function fetchBedrockGroups(): Promise<BedrockResult<BedrockGroupSummary[]>> {
  const result = await modelsRequest<{ groups?: BedrockGroupSummary[] }>(groupsPath, { method: "GET" });
  if (result.kind !== "ok") {
    return result;
  }

  return Array.isArray(result.response.groups) ? { kind: "ok", response: result.response.groups } : { kind: "error", message: "" };
}

/** Calls `GET /api/admin/bedrock/models/groups/{group}`: every model by family, what the policy allows and the hash to send back. */
export function fetchBedrockGroup(group: string): Promise<BedrockResult<BedrockGroupDetail>> {
  return modelsRequest<BedrockGroupDetail>(groupPath(group), { method: "GET" });
}

/** Calls `PUT /api/admin/bedrock/models/groups/{group}`: replaces the group's whole selection. */
export function updateBedrockGroup(
  group: string,
  selection: BedrockSelection,
  expectedPolicyHash: string,
  confirmEmpty: boolean,
): Promise<BedrockResult<BedrockGroupDetail>> {
  return modelsRequest<BedrockGroupDetail>(groupPath(group), { method: "PUT", body: JSON.stringify({ ...selection, expectedPolicyHash, confirmEmpty }) });
}

/** Calls `POST …/takeover/preview`: read-only; what replacing a foreign policy with this selection would change. */
export function previewBedrockTakeover(group: string, selection: BedrockSelection): Promise<BedrockResult<BedrockTakeoverPreview>> {
  return modelsRequest<BedrockTakeoverPreview>(`${groupPath(group)}/takeover/preview`, { method: "POST", body: JSON.stringify(selection) });
}

/** Calls `POST …/takeover`: replaces a foreign group's policy with the portal-managed one. `expectedPolicyHash` is the preview's `currentPolicyHash`. */
export function takeOverBedrockGroup(
  group: string,
  selection: BedrockSelection,
  expectedPolicyHash: string,
  confirmEmpty: boolean,
): Promise<BedrockResult<BedrockGroupDetail>> {
  return modelsRequest<BedrockGroupDetail>(`${groupPath(group)}/takeover`, {
    method: "POST",
    body: JSON.stringify({ ...selection, expectedPolicyHash, confirmTakeover: true, confirmEmpty }),
  });
}
